import { ref, computed } from 'vue';
import {
  addMarketFavorite,
  removeMarketFavorite,
  getMarketFavoritePage,
  checkMarketFavorite,
} from '@/api/user';
import { hasAuthSession } from '@/utils/auth-token';
import {
  getCurrentLoginReturnPath,
  promptAuthLogin,
  subscribeAuthState,
} from '@/utils/auth-session';
import { trackAnalyticsLog } from '@/utils/analytics-track';

/** 当前会话收藏集合 ID（登录后从 page 接口同步） */
const favoriteIdSet = ref(new Set());
const loaded = ref(false);
const loading = ref(false);
/** page 接口不可用时为 true（404 / 未部署），仍可用 add/remove + check 恢复图标 */
const pageApiUnavailable = ref(false);
const checkedIdSet = new Set();
const toggleInflight = new Set();
const toggleThrottleUntil = new Map();
const remoteCheckSkipUntil = new Map();
const TOGGLE_THROTTLE_MS = 600;
const REMOTE_CHECK_SKIP_MS = 3000;
let loadPromise = null;
let checkInflight = null;

function shouldSkipToggle(sid) {
  if (toggleInflight.has(sid)) return true;
  return Date.now() < (toggleThrottleUntil.get(sid) ?? 0);
}

function markToggleStarted(sid) {
  toggleInflight.add(sid);
  toggleThrottleUntil.set(sid, Date.now() + TOGGLE_THROTTLE_MS);
}

function markToggleFinished(sid) {
  toggleInflight.delete(sid);
}

function markRemoteCheckSkip(sid) {
  remoteCheckSkipUntil.set(sid, Date.now() + REMOTE_CHECK_SKIP_MS);
}

function shouldSkipRemoteCheck(sid) {
  const until = remoteCheckSkipUntil.get(sid) ?? 0;
  if (Date.now() >= until) {
    remoteCheckSkipUntil.delete(sid);
    return false;
  }
  return true;
}

function normalizeFavoritePage(data) {
  if (Array.isArray(data?.rows)) {
    return { rows: data.rows, total: Number(data.total) || data.rows.length };
  }
  if (Array.isArray(data?.data?.rows)) {
    return { rows: data.data.rows, total: Number(data.data.total) || data.data.rows.length };
  }
  if (Array.isArray(data)) {
    return { rows: data, total: data.length };
  }
  return { rows: [], total: 0 };
}

function isWatchlistPageApiUnavailable(err) {
  const status = err?.response?.status;
  if (status === 404) return true;
  const msg = String(err?.message ?? err?.response?.data?.msg ?? '');
  return /404\s*NOT_FOUND/i.test(msg);
}

function normalizeCollectionId(id) {
  return String(id ?? '').trim();
}

function applyFavoritePageRows(rows) {
  const serverSet = new Set(
    rows.map((row) => normalizeCollectionId(row.collectionId)).filter(Boolean)
  );
  const current = favoriteIdSet.value;
  const next = new Set(serverSet);
  for (const sid of toggleInflight) {
    if (current.has(sid)) next.add(sid);
    else next.delete(sid);
  }
  favoriteIdSet.value = next;
}

async function loadFavorites() {
  if (loadPromise) return loadPromise;
  if (pageApiUnavailable.value) return;

  loadPromise = (async () => {
    if (!hasAuthSession()) {
      favoriteIdSet.value = new Set();
      loaded.value = true;
      return;
    }

    loading.value = true;
    try {
      const data = await getMarketFavoritePage();
      pageApiUnavailable.value = false;
      const { rows } = normalizeFavoritePage(data);
      applyFavoritePageRows(rows);
      loaded.value = true;
    } catch (err) {
      if (isWatchlistPageApiUnavailable(err)) {
        pageApiUnavailable.value = true;
        loaded.value = true;
        console.warn('[watchlist] page API unavailable, falling back to check API');
        return;
      }
      console.warn('[watchlist] load favorites failed', err);
      loaded.value = true;
    } finally {
      loading.value = false;
    }
  })();

  try {
    await loadPromise;
  } finally {
    loadPromise = null;
  }
}

/**
 * page 不可用时，对可见卡片 batch 调用 GET /market-favorite/check 恢复收藏态
 * @param {string[]} collectionIds
 */
async function syncFavoriteChecks(collectionIds) {
  if (!hasAuthSession()) return;
  if (!pageApiUnavailable.value) return;

  const pending = [...new Set(collectionIds.map(normalizeCollectionId).filter(Boolean))].filter(
    (id) => !checkedIdSet.has(id) && !toggleInflight.has(id) && !shouldSkipRemoteCheck(id)
  );
  if (!pending.length) return;

  if (checkInflight) return checkInflight;

  checkInflight = (async () => {
    const results = await Promise.allSettled(
      pending.map(async (id) => {
        checkedIdSet.add(id);
        const saved = await checkMarketFavorite(id);
        return { id, saved: saved === true };
      })
    );

    const next = new Set(favoriteIdSet.value);
    for (const result of results) {
      if (result.status !== 'fulfilled') continue;
      const { id, saved } = result.value;
      if (shouldSkipRemoteCheck(id) || toggleInflight.has(id)) continue;
      if (saved) next.add(id);
    }
    favoriteIdSet.value = next;
  })();

  try {
    await checkInflight;
  } finally {
    checkInflight = null;
  }
}

let authLoggedIn = hasAuthSession();

/** 同会话换号（如 MetaMask 切账户）：登录态不变，须主动重拉收藏 */
export function reloadWatchlistOnAccountSwitch() {
  favoriteIdSet.value = new Set();
  loaded.value = false;
  pageApiUnavailable.value = false;
  checkedIdSet.clear();
  toggleInflight.clear();
  toggleThrottleUntil.clear();
  remoteCheckSkipUntil.clear();
  if (hasAuthSession()) {
    loadFavorites().catch(() => {});
  }
}

queueMicrotask(() => {
  if (authLoggedIn) {
    loadFavorites().catch(() => {});
  }

  subscribeAuthState((loggedIn) => {
    if (loggedIn && !authLoggedIn) {
      pageApiUnavailable.value = false;
      checkedIdSet.clear();
      loadFavorites().catch(() => {});
    } else if (!loggedIn) {
      favoriteIdSet.value = new Set();
      loaded.value = false;
      pageApiUnavailable.value = false;
      checkedIdSet.clear();
      toggleInflight.clear();
      toggleThrottleUntil.clear();
      remoteCheckSkipUntil.clear();
    }
    authLoggedIn = loggedIn;
  });
});

export function useMarketWatchlist() {
  function isWatchlisted(id) {
    const sid = normalizeCollectionId(id);
    if (!sid) return false;
    return favoriteIdSet.value.has(sid);
  }

  async function toggle(id) {
    const sid = normalizeCollectionId(id);
    if (!sid) return;

    if (!promptAuthLogin(getCurrentLoginReturnPath())) return;

    if (shouldSkipToggle(sid)) return;

    const wasSaved = isWatchlisted(sid);
    markToggleStarted(sid);
    markRemoteCheckSkip(sid);

    const next = new Set(favoriteIdSet.value);
    if (wasSaved) next.delete(sid);
    else next.add(sid);
    favoriteIdSet.value = next;
    checkedIdSet.add(sid);

    try {
      if (wasSaved) await removeMarketFavorite(sid);
      else await addMarketFavorite(sid);
      trackAnalyticsLog({
        key: 'watchlist.toggle',
        label: '收藏切换',
        value: { action: wasSaved ? 'remove' : 'add', marketId: sid },
      });
    } catch (err) {
      const rollback = new Set(favoriteIdSet.value);
      if (wasSaved) rollback.add(sid);
      else rollback.delete(sid);
      favoriteIdSet.value = rollback;
      throw err;
    } finally {
      markToggleFinished(sid);
    }
  }

  return {
    watchlistIds: computed(() => [...favoriteIdSet.value]),
    loaded: computed(() => loaded.value),
    loading: computed(() => loading.value),
    pageApiUnavailable: computed(() => pageApiUnavailable.value),
    isWatchlisted,
    toggle,
    refresh: loadFavorites,
    syncFavoriteChecks,
  };
}
